/**
 * LocationCell — outline map-pin + governorate (bold) + truncated detail + dir="auto"
 *
 * Bold locality = the order's governorate name (resolved from governorateId via
 * the existing governorates reference list). The structured address (area, street,
 * building, floor, apartment, landmark) forms the truncated detail line. Falls
 * back to `area`, then the freetext `buyerAddress`, when a governorate name isn't
 * available.
 */

import type { OrderAddressView } from "@/lib/api/types";
import { useLocale } from "@/lib/i18n/intl";
import { joinLocalized } from "@/lib/formatting/list";
import { useGovernorateName } from "./useGovernorateName";

type Props = {
  address: OrderAddressView | null;
  buyerAddress: string;
  governorateId?: string | null;
};

function PinIcon() {
  return (
    <svg
      aria-hidden="true"
      className="mt-0.5 h-[15px] w-[15px] shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--text-disabled)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

export function LocationCell({ address, buyerAddress, governorateId }: Props) {
  const govName = useGovernorateName()(governorateId);
  const locale = useLocale();

  // Bold locality: governorate name, else the area, else nothing.
  const locality = govName ?? address?.area ?? null;

  // Detail line: the structured parts (area included only when governorate is
  // the bold label), else the freetext address.
  const detailParts: string[] = [];
  if (address) {
    if (govName && address.area) detailParts.push(address.area);
    if (address.street) detailParts.push(address.street);
    if (address.building) detailParts.push(address.building);
    if (address.floor) detailParts.push(address.floor);
    if (address.apartment) detailParts.push(address.apartment);
    if (address.landmark) detailParts.push(address.landmark);
  }
  let detail = joinLocalized(detailParts, locale);
  if (!detail && !address && buyerAddress) detail = buyerAddress;

  if (locality) {
    return (
      <div className="flex items-start gap-1.5 min-w-0" dir="auto">
        <PinIcon />
        <div className="min-w-0">
          <p className="truncate text-[12.5px] font-bold leading-snug text-[color:var(--text-primary)]">
            {locality}
          </p>
          {detail ? (
            <p className="truncate text-[11.5px] leading-snug text-[color:var(--text-muted)]">
              {detail}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (buyerAddress) {
    return (
      <div className="flex items-start gap-1.5 min-w-0" dir="auto">
        <PinIcon />
        <p className="min-w-0 truncate text-[11.5px] leading-snug text-[color:var(--text-muted)]">
          {buyerAddress}
        </p>
      </div>
    );
  }

  return null;
}
